import React, {useEffect, useMemo, useState} from 'react';
import {BudgetingType} from '../../BudgetingType';
import {VarianceType} from "../../VarianceType";
import {
    BudgetComponentType,
    GetFinancialValuesQuery,
    useGetFinancialValuesLazyQuery,
    useGetTableViewForStarredAccountsByPropertyIdAndVersionIdsLazyQuery
} from '../../__generated__/generated_types';
import {useChartOfAccounts} from '../../contexts/chartofaccounts/ChartOfAccountsContext';
import {createRequestVariables} from '../../contexts/chartofaccounts/CreateFinancialValuesQueryVariables';
import {useProperties} from '../../contexts/properties/PropertiesContext';
import {useSettings} from '../../contexts/settings/SettingsContext';
import {useVersions} from '../../contexts/versions/VersionsContext';
import {getMonths} from '../../utils/months-helper';
import Loading from '../loading/Loadiing';
import YearSelector from '../year-selector/YearSelector';
import './AnnualSummary.scss';
import {computeStarredVisibility, fillLineData, StarredFilter} from './Helpers';
import {Line, LineProps} from './Line';
import useYearDefinitions from './YearDefinitions';


export interface AnnualSummaryProps {
    tab: BudgetingType,
    refreshTriggered: boolean;
    refreshCompletedCallback: () => void;
    budgetYear: number;
    starredOnly: boolean;
}


export const AnnualSummary: React.FC<AnnualSummaryProps> = (props: AnnualSummaryProps) => {
    const [varianceSelectedValue, setVarianceSelectedValue] = useState(VarianceType.VARIANCE_PERCENT);
    const isReforecast = props.tab === BudgetingType.REFORECAST;
    const {currentProperty: property} = useProperties();
    const months = useMemo(() => getMonths(property), [property]);
    const {year: currentYear, startReforecastMonthIndex} = useSettings();
    const {getModelVersions} = useVersions();
    const {currentDefinition: referenceYear} = useYearDefinitions();
    const {chartOfAccounts, getFinancialEntityById} = useChartOfAccounts();
    const { definitions, setCurrentDefinition } = useYearDefinitions();

    const [getFinancialValues, {loading, error, data}] = useGetFinancialValuesLazyQuery({
        fetchPolicy: "no-cache",
    });

    const [fetchStarredAccounts, {data: starredAccountsData, loading: starredAccountsLoading}] = useGetTableViewForStarredAccountsByPropertyIdAndVersionIdsLazyQuery({
        fetchPolicy: "no-cache",
    });

    const [starredAccountIds, setStarredAccountIds] = useState<Set<string>>(new Set());

    useEffect(() => {
        if (!props.starredOnly || !property?.id) return;

        const {reforecastVersionId, nextYearBudgetVersionId} = getModelVersions(currentYear);

        fetchStarredAccounts({
            variables: {
                propertyId: property.id,
                versionIds: [reforecastVersionId, nextYearBudgetVersionId],
                financialTotalValueArgInput: [],
                budgetYear: props.budgetYear,
            }
        });
    }, [props.starredOnly, property?.id, currentYear, getModelVersions, props.budgetYear, props.refreshTriggered]);

    useEffect(() => {
        if (!starredAccountsData?.getTableViewForStarredAccountsByPropertyIdAndVersionIds) return;

        setStarredAccountIds(new Set(
            starredAccountsData.getTableViewForStarredAccountsByPropertyIdAndVersionIds
                .filter(row => isReforecast ? row.isReforecastVersionStarred : row.isBudgetVersionStarred)
                .map(row => row.accountId)
        ));
    }, [starredAccountsData, isReforecast, starredAccountsLoading]);

    const starredFilter: StarredFilter | undefined = useMemo(() => (
        props.starredOnly ? computeStarredVisibility(starredAccountIds, getFinancialEntityById) : undefined
    ), [props.starredOnly, starredAccountIds, getFinancialEntityById]);

    function onStarChanged(accountId: string, isStarred: boolean) {
        setStarredAccountIds(prev => {
            const next = new Set(prev);
            if (isStarred) {
                next.add(accountId);
            } else {
                next.delete(accountId);
            }
            return next;
        });
    }

    useEffect(() => {
        if (!chartOfAccounts || !property) {
            return;
        }

        getFinancialValues({
            variables: createRequestVariables(chartOfAccounts.concat(chartOfAccounts.flatMap(row => row.children)), property.id, startReforecastMonthIndex, currentYear, getModelVersions, props.budgetYear)
        });
    }, [chartOfAccounts, property]
    );

    useEffect(() => {
        // acquisition properties can only be compared to T12
        if (property?.isAcquisitionProperty) {
            const newYearDefinition = definitions[1];
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            setCurrentDefinition(newYearDefinition!);
        }
    }, []);

    if (loading) {
        return <Loading />;
    }

    if (error) {
        return <>{error}</>;
    }

    function toLineProps(data: GetFinancialValuesQuery): LineProps[] {
        const lineProps: LineProps[] = [];

        if (!chartOfAccounts || loading) {
            return [];
        }
        const {actualVersionId, reforecastVersionId, nextYearBudgetVersionId, currentYearTotalVersionId} = getModelVersions(currentYear);
        const valuesByEntityAndVersion = data.financialValues.financialValues.groupBy(row => row.entityId + "#" + row.versionId);
        const totalValuesByEntityAndVersion = data.financialValues.financialTotalValues.groupBy(row => row.entityId + "#" + row.versionId);
        chartOfAccounts.forEach(component => {
            const isPositiveGood = component.budgetComponentType === BudgetComponentType.Income;
            let previousTotal = Number(totalValuesByEntityAndVersion[component.id + "#" + referenceYear.versionId]?.firstElement?.value ?? "0");
            if(referenceYear.versionId === currentYearTotalVersionId) {
                const reforecastValues = [
                    ...(valuesByEntityAndVersion[component.id + "#" + actualVersionId] ?? []),
                    ...(valuesByEntityAndVersion[component.id + "#" + reforecastVersionId] ?? [])
                ];
                previousTotal = reforecastValues.map(rv => Number(rv.value ?? 0.0)).sum();
            }

            const values = isReforecast ?
                [
                    ...(valuesByEntityAndVersion[component.id + "#" + actualVersionId] ?? []),
                    ...(valuesByEntityAndVersion[component.id + "#" + reforecastVersionId] ?? [])
                ]
                :
                valuesByEntityAndVersion[component.id + "#" + nextYearBudgetVersionId] ?? [];
            const total = values.map(v => Number(v.value ?? 0.0)).sum();
            const lineData = fillLineData(
                values.map(row => ({monthIndex: row.monthIndex, value: Number(row.value)})),
                total,
                previousTotal,
                component
            );

            component.children.forEach(category => {
                if (starredFilter && !starredFilter.visibleEntityIds.has(category.id)) return;

                let previousTotal = Number(totalValuesByEntityAndVersion[category.id + "#" + referenceYear.versionId]?.firstElement?.value ?? "0");
                if(referenceYear.versionId === currentYearTotalVersionId) {
                    const reforecastValues = [
                        ...(valuesByEntityAndVersion[category.id + "#" + actualVersionId] ?? []),
                        ...(valuesByEntityAndVersion[category.id + "#" + reforecastVersionId] ?? [])
                    ];
                    previousTotal = reforecastValues.map(rv => Number(rv.value ?? 0.0)).sum();
                }

                const values = isReforecast ? [
                    ...(valuesByEntityAndVersion[category.id + "#" + actualVersionId] ?? []),
                    ...(valuesByEntityAndVersion[category.id + "#" + reforecastVersionId] ?? [])
                ]
                    :
                    valuesByEntityAndVersion[category.id + "#" + nextYearBudgetVersionId] ?? [];
                const total = values.map(v => Number(v.value ?? 0.0)).sum();
                const lineData = fillLineData(
                    values.map(row => ({monthIndex: row.monthIndex, value: Number(row.value)})),
                    total,
                    previousTotal,
                    category
                );


                if (lineData) {
                    // lineData.categoryId = category.id;
                    lineData.financialEntity = category;

                    lineProps.push({
                        isPositiveGood,
                        data: lineData,
                        tab: props.tab,
                        varianceType: varianceSelectedValue,
                        refreshTriggered: props.refreshTriggered,
                        refreshCompletedCallback: props.refreshCompletedCallback,
                        budgetYear: props.budgetYear,
                        starredFilter,
                    });
                }
            });
            if (lineData && !starredFilter) {
                // lineData.isComponent = true;
                // lineData.componentId = component.id;
                lineData.financialEntity = component;
                lineProps.push({
                    isPositiveGood,
                    data: lineData,
                    tab: props.tab,
                    varianceType: varianceSelectedValue,
                    refreshTriggered: props.refreshTriggered,
                    refreshCompletedCallback: props.refreshCompletedCallback,
                    budgetYear: props.budgetYear,
                    starredFilter,
                });
            }

        });

        return lineProps;
    }

    function isRfcstStartMonth(monthIndex: number): boolean {
        return isReforecast && property?.reforecastStartMonthIndex === monthIndex;
    }

    function getHeaderCellMetaLabel(monthIndex: number): string {
        if (property?.isAcquisitionProperty) {
            return property.acquisitionBudgetYear ? "Yr 1" : "";
        } else {
            if (isRfcstStartMonth(monthIndex)) {
                return "RFCST";
            }
            if (monthIndex === 0) {
                return isReforecast ? "ACTUALS" : "BUDGET";
            }
        }
        return "";
    }

    const showStarredAccountsLoading = props.starredOnly && starredAccountsLoading;

    const showNoStarredAccounts =
        data &&
        props.starredOnly &&
        !starredAccountsLoading &&
        starredAccountsData &&
        starredFilter?.visibleEntityIds.size === 0;

    return (
        <div className="as-table-wrapper">
            <table className='table-annual-summary'>
                <thead className="table-annual-summary-head">
                    <tr className="as-trow">
                        <th className="th-description"> Description </th>
                        {months.map((month, index) =>
                            <th
                                key={index}
                                className={`th-months ${isRfcstStartMonth(index) && "rfcst-month-start-cell"}`}
                            >
                                <span className="cell-meta-label">{getHeaderCellMetaLabel(index)}</span>
                                {month}
                            </th>
                        )}
                        <th className="as-header-total"> Total </th>
                        <th className="as-header-reference-total">
                            {property?.isAcquisitionProperty ?
                                <th>T12</th>
                                : <YearSelector variant="standard" />
                            }
                        </th>
                        <th className="th-variance">
                            <select className="annual-summary-select" onChange={(event) => setVarianceSelectedValue(event.target.value as VarianceType)}>
                                <option value={VarianceType.VARIANCE_PERCENT}> Var % </option>
                                <option value={VarianceType.VARIANCE_AMOUNT}> Var $ </option>
                            </select>
                        </th>
                        <th className="th-notes" style={{textAlign: "center"}}>Notes</th>
                    </tr>

                </thead>
                <tbody className="as-tbody">
                    {showStarredAccountsLoading &&
                        <tr>
                            <td colSpan={months.length + 5} className="text-center">
                                <Loading />
                            </td>
                        </tr>
                    }
                    {showNoStarredAccounts &&
                        <tr>
                            <td colSpan={months.length + 5} className="text-center">
                                No starred accounts for this property.
                            </td>
                        </tr>
                    }
                    {data && !showStarredAccountsLoading && ((() => {
                        const lineProps = toLineProps(data);

                        return lineProps.map((line) =>
                            <Line
                                key={line.data.financialEntity.id}
                                data={line.data}
                                isPositiveGood={line.isPositiveGood}
                                tab={line.tab}
                                varianceType={varianceSelectedValue}
                                refreshTriggered={props.refreshTriggered}
                                refreshCompletedCallback={props.refreshCompletedCallback}
                                budgetYear={props.budgetYear}
                                starredFilter={line.starredFilter}
                                onStarChanged={onStarChanged}
                            />
                        );
                    })()
                    )}
                </tbody>
            </table>
        </div>
    );
};
